// React
import React, { useEffect, useState, Fragment } from 'react';

// Material UI
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
// Removed modal & PIN-related imports

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { RootStore } from '../../../Store';
import { getPreorderEventOrders, updatePreorderOrderStatus } from '../../../actions/preorderEventActions/PreorderEventActions';

// Components
import Pagination from '../utils/ResultsPagination';
import PreorderOrderDetailsDialog from './PreorderOrderDetailsDialog';
// Notes component removed (not used in preorder orders view)

// Config
import { getProjectConfig } from '../../../getProjectConfig';

// Translations
import Moment from 'moment';
import jwtDecode from 'jwt-decode';
import { useTranslation } from 'react-i18next';

// Styles
import useStyles from './PreorderEventOrdersListStyles';

interface PreorderEventOrdersListProps {
  preorderEventId: number;
  onClearSelection: () => void;
}

const PreorderEventOrdersList: React.FC<PreorderEventOrdersListProps> = ({ preorderEventId, onClearSelection }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('global');

  const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
  const { preorderEventOrders, preorderEventDetails, loading, actionStatus } = preorderEventState;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLoading, setCurrentLoading] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [pendingUpdate, setPendingUpdate] = useState<{ orderId: number; colStatus: string; newStatus: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  
  // Debounce search term (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Load orders for preorder event / pagination / search
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let page = params.get('page');
    const pNumber = Number(page);

    const qParams: any = {
      page: page ? pNumber - 1 : 0,
      size: getProjectConfig().PAGES_SIZE,
      paged: true,
    };

    // Smart search: Check if search term matches an item name
    if (debouncedSearchTerm.trim()) {
      const matchedItem = preorderEventDetails?.items?.find(
        item => item.name.toLowerCase() === debouncedSearchTerm.trim().toLowerCase()
      );
      
      if (matchedItem) {
        // If search matches an item name, send itemId
        qParams.itemId = matchedItem.id;
      } else {
        // Otherwise, send as searchTermUserInfo
        qParams.searchTermUserInfo = debouncedSearchTerm.trim();
      }
    }

    dispatch(getPreorderEventOrders(preorderEventId, qParams));
    setCurrentPage(page ? pNumber : 1);
    // eslint-disable-next-line
  }, [window.location.search, preorderEventId, debouncedSearchTerm]);

  // Reset loading when orders arrive OR when update action completes
  useEffect(() => {
    setCurrentLoading('');
    // eslint-disable-next-line
  }, [preorderEventOrders]);

  // Reset loading when preorder action completes
  useEffect(() => {
    if (loading === '' && currentLoading !== '') {
      setCurrentLoading('');
    }
    // eslint-disable-next-line
  }, [loading]);

  // Refetch on successful status update
  useEffect(() => {
    if (actionStatus?.status === 'success' && currentLoading !== '') {
      setCurrentLoading(''); // Clear loading immediately
      const qParams: any = {
        page: currentPage - 1,
        size: getProjectConfig().PAGES_SIZE,
        paged: true,
      };

      // Include search parameters when refetching
      if (debouncedSearchTerm.trim()) {
        const matchedItem = preorderEventDetails?.items?.find(
          item => item.name.toLowerCase() === debouncedSearchTerm.trim().toLowerCase()
        );
        
        if (matchedItem) {
          qParams.itemId = matchedItem.id;
        } else {
          qParams.searchTermUserInfo = debouncedSearchTerm.trim();
        }
      }

      dispatch(getPreorderEventOrders(preorderEventId, qParams));
    }
    if (actionStatus?.status === 'error') {
      setCurrentLoading('');
    }
    // eslint-disable-next-line
  }, [actionStatus]);

  // Auth / role logic
  const decodedToken: any = localStorage.token !== undefined && localStorage.token !== '' ? jwtDecode(localStorage.token) : false;
  const isAdminSingles = (decodedToken) ? decodedToken.auth.includes('ROLE_ADMIN_SINGLES') : false;

  // Removed modal & PIN handlers (simplified preorder orders flow)

  const isDiabledCard = (cStatus: string, oStatus: string) => {
    const orderStatus = oStatus.toLocaleLowerCase();
    if (cStatus === 'procesando' && orderStatus === 'orden recibida') return false;
    if (cStatus === 'listo para recoger' && orderStatus === 'procesando') return false;
    if (cStatus === 'completado' && orderStatus === 'listo para recoger') return false;
    return true;
  };

  const isCurrentStatus = (cStatus: string, oStatus: string) => cStatus === oStatus.toLocaleLowerCase();

  const updateOrderOnClick = (oId: number, colStatus: string, oNewStatus: string) => {
    if (oId && oNewStatus) {
      // Show confirmation dialog for moving to "Procesando" status
      if (oNewStatus === 'Procesando') {
        setPendingUpdate({ orderId: oId, colStatus, newStatus: oNewStatus });
        setConfirmDialogOpen(true);
        return;
      }
      
      setCurrentLoading(`${colStatus}-${oId}`);
      dispatch(updatePreorderOrderStatus({ orderId: oId, status: oNewStatus }));
    }
  };

  const handleConfirmUpdate = () => {
    if (pendingUpdate) {
      setCurrentLoading(`${pendingUpdate.colStatus}-${pendingUpdate.orderId}`);
      dispatch(updatePreorderOrderStatus({ orderId: pendingUpdate.orderId, status: pendingUpdate.newStatus }));
    }
    setConfirmDialogOpen(false);
    setPendingUpdate(null);
  };

  const handleCancelUpdate = () => {
    setConfirmDialogOpen(false);
    setPendingUpdate(null);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleOpenDetails = (order: any) => {
    setSelectedOrder(order);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedOrder(null);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6" color="primary">
          {t('preorders.orders')} (ID: {preorderEventId}) {loading === 'GET_PREORDER_EVENT_ORDERS' && <CircularProgress size={20} />}
        </Typography>
        <Button variant="outlined" color="primary" onClick={onClearSelection} className={classes.backButton}>
          {t('common.go-back')}
        </Button>
      </div>
      
      {/* Search Section */}
      <div className={classes.searchSection}>
        <Typography variant="h6" color="textSecondary" style={{ marginBottom: '8px' }}>
          <SearchIcon style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          {t('preorders.search')} & {t('common.filters')}
        </Typography>
        <TextField
          placeholder={t('preorders.search-placeholder')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading === 'GET_PREORDER_EVENT_ORDERS' && searchTerm ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon color="action" />
                )}
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {debouncedSearchTerm && (
          <Typography variant="body2" color="textSecondary" className={classes.searchResults}>
            {t('preorders.search-results', { count: preorderEventOrders?.content?.length || 0 })}
          </Typography>
        )}
      </div>
      
      <Divider />

      {preorderEventOrders?.content && preorderEventOrders.content.length > 0 ? (
        <>
          {preorderEventOrders.content.map(order => {
            // Calculate payment info
            const totalPrice = order.totalPrice || 0;
            const partialPayments = order.partialPayment || [];
            const totalPaidAmount = partialPayments.reduce((sum: number, payment: any) => sum + payment.paymentAmount, 0);
            const remainingAmount = totalPrice - totalPaidAmount;
            const isFullyPaid = order.paid || remainingAmount <= 0;
            
            return (
            <Fragment key={order.id}>
              <Grid container spacing={2} className={classes.orderRow}>
                <Grid item xs={12} sm={12} md={4}>
                  <Typography className={classes.boldText}>{order.code}</Typography>
                  <Typography variant="caption">ID: {order.id}</Typography>
                  <Typography>{t('orders.client-name')}: {order.customerName || '-'}</Typography>
                  <Typography>{t('users.email')}: {order.email || '-'}</Typography>
                  <Typography>{t('orders.created')}: {order.creationDate ? Moment(order.creationDate).format('MM/DD/YYYY HH:mm') : '-'}</Typography>
                  
                  {/* View Details Button */}
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => handleOpenDetails(order)}
                    style={{ marginTop: '8px' }}
                  >
                    Ver más detalles
                  </Button>
                  
                  {/* Payment Information */}
                  <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {t('preorders.payment-total')}: ₡{totalPrice.toLocaleString()}
                    </Typography>
                    {partialPayments.length > 0 ? (
                      <>
                        <Typography variant="caption" color="primary">
                          {t('preorders.payment-paid')}: ₡{totalPaidAmount.toLocaleString()} 
                          {partialPayments.length > 1 ? ` (${partialPayments.length} ${t('preorders.payment-multiple')})` : ` (${Moment(partialPayments[0].paymentDate).format('MM/DD/YYYY')})`}
                        </Typography>
                        <br />
                        <Typography variant="caption" color={isFullyPaid ? 'primary' : 'error'}>
                          {isFullyPaid ? t('preorders.payment-complete') : `${t('preorders.payment-pending')}: ₡${remainingAmount.toLocaleString()}`}
                        </Typography>
                      </>
                    ) : order.paid ? (
                      <Typography variant="caption" color="primary">
                        {t('preorders.payment-complete')}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="error">
                        {t('preorders.payment-none')}
                      </Typography>
                    )}
                  </div>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Card className={`${isDiabledCard('orden recibida', order.status) ? classes.disabledCard : ''} ${isCurrentStatus('orden recibida', order.status) ? classes.currentStatus : ''}`}>
                    <CardActionArea disabled={isDiabledCard('orden recibida', order.status) || (currentLoading === `orden recibida-${order.id}`)}>
                      <CardContent>
                        <Typography className={classes.ctaTitle}>Recibido</Typography>
                        {(currentLoading === `orden recibida-${order.id}`) && <Typography variant="caption">{t('orders.processing')}</Typography>}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Card className={`${isDiabledCard('procesando', order.status) ? classes.disabledCard : ''} ${isCurrentStatus('procesando', order.status) ? classes.currentStatus : ''}`}>
                    <CardActionArea disabled={isDiabledCard('procesando', order.status) || (currentLoading === `procesando-${order.id}`)} onClick={() => updateOrderOnClick(order.id, 'procesando', 'Procesando')}>
                      <CardContent>
                        <Typography className={classes.ctaTitle}>{!isDiabledCard('procesando', order.status) && <span>{t('orders.move-to')}<br /></span>}Procesando</Typography>
                        {(order.processedBy) && <>
                          <Typography variant="caption" className={classes.breakWord}>{order.processedBy}</Typography><br />
                          <Typography variant="caption">{Moment(order.processedByDate).format('MM/DD/YYYY HH:mm')}</Typography>
                        </>}
                        {(currentLoading === `procesando-${order.id}` && actionStatus?.status !== 'error') && <Typography variant="caption">{t('orders.processing')}</Typography>}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Card className={`${(isDiabledCard('listo para recoger', order.status) || isAdminSingles) ? classes.disabledCard : ''} ${isCurrentStatus('listo para recoger', order.status) ? classes.currentStatus : ''}`}>
                    <CardActionArea disabled={isDiabledCard('listo para recoger', order.status) || (currentLoading === `listo para recoger-${order.id}`) || isAdminSingles} onClick={() => updateOrderOnClick(order.id, 'listo para recoger', 'Listo para Recoger')}>
                      <CardContent>
                        <Typography className={classes.ctaTitle}>{!isDiabledCard('listo para recoger', order.status) && <span>{t('orders.move-to')}<br /></span>}Recibido por Tienda</Typography>
                        {(order.readyPickupBy) && <>
                          <Typography variant="caption" className={classes.breakWord}>{order.readyPickupBy}</Typography><br />
                          <Typography variant="caption">{Moment(order.readyPickupByDate).format('MM/DD/YYYY HH:mm')}</Typography>
                        </>}
                        {(currentLoading === `listo para recoger-${order.id}`) && <Typography variant="caption">{t('orders.processing')}</Typography>}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Card className={`${(isDiabledCard('completado', order.status) || isAdminSingles) ? classes.disabledCard : ''} ${isCurrentStatus('completado', order.status) ? classes.currentStatus : ''}`}>
                    <CardActionArea disabled={isDiabledCard('completado', order.status) || (currentLoading === `completado-${order.id}`) || isAdminSingles} onClick={() => updateOrderOnClick(order.id, 'completado', 'Completado')}>
                      <CardContent>
                        <Typography className={classes.ctaTitle}>{!isDiabledCard('completado', order.status) && <span>{t('orders.move-to')}<br /></span>}Completado</Typography>
                        {(order.completedBy) && <>
                          <Typography variant="caption" className={classes.breakWord}>{order.completedBy}</Typography><br />
                          <Typography variant="caption">{Moment(order.completedDate).format('MM/DD/YYYY HH:mm')}</Typography>
                        </>}
                        {(currentLoading === `completado-${order.id}`) && <Typography variant="caption">{t('orders.processing')}</Typography>}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
              <Divider />
                {/* Notes modal removed for preorder orders */}
            </Fragment>
            )
          })}
          {!searchTerm && preorderEventOrders?.totalPages && preorderEventOrders.totalPages > 1 && (
            <div className={classes.pagination}>
              <Pagination current={currentPage} path={`/admin/preorders`} pagesNumber={preorderEventOrders.totalPages} />
            </div>
          )}
        </>
      ) : preorderEventOrders?.content && preorderEventOrders.content.length > 0 && searchTerm ? (
        <Typography variant="body1" color="textSecondary">
          {t('preorders.no-search-results', { searchTerm })}
        </Typography>
      ) : (
        <Typography variant="body1" color="textSecondary">{t('preorders.no-orders')}</Typography>
      )}

      {/* Confirmation Dialog for Processing Status */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelUpdate}
        aria-labelledby="confirm-processing-dialog-title"
        aria-describedby="confirm-processing-dialog-description"
      >
        <DialogTitle id="confirm-processing-dialog-title">
          {t('preorders.confirm-processing-title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-processing-dialog-description">
            {t('preorders.confirm-processing-message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpdate} color="default">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary" variant="contained">
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <PreorderOrderDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        order={selectedOrder}
      />
    </div>
  );
};

export default PreorderEventOrdersList;
